// Checks only the fields the UI branches on; unknown fields are ignored on purpose.

import { firstLetter } from './format'

const DEFAULT_POLL_MS = 15000
const DEFAULT_STALE_MS = 45000
const MIN_POLL_MS = 5000

function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]'
}

function contractError(reason) {
    return { code: 0, contract: reason, message: 'Сервер вернул данные в неожиданном формате' }
}

// The interceptor rejects with the body `{ code, message }`, or with the AxiosError when the body is empty.
export function errorStatus(error) {
    return Number(error && error.code) || (error && error.response && error.response.status) || 0
}

export function errorMessage(error, fallback) {
    return (error && typeof error.message === 'string' && error.message) || fallback
}

function isId(value) {
    if (typeof value === 'number') return Number.isInteger(value) && value > 0
    return typeof value === 'string' && value.trim() !== ''
}

function parseTime(value) {
    if (typeof value !== 'string') return null
    const ms = Date.parse(value)
    return Number.isFinite(ms) ? ms : null
}

function positiveMs(value, fallback, min) {
    const ms = Number(value)
    return Number.isFinite(ms) && ms >= min ? ms : fallback
}

function unwrap(body) {
    if (!isPlainObject(body) || !isPlainObject(body.data)) throw contractError('envelope')
    return body.data
}

function normalizeTarget(target) {
    if (!isPlainObject(target) || !isId(target.company_id)) return null
    return target
}

function normalizeCompany(raw) {
    if (!isPlainObject(raw) || !isId(raw.company_id)) throw contractError('company_id')
    const name = typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : 'Без названия'
    const unavailable = raw.status === 'unavailable'
    // A bad count must never turn into a fake zero.
    const waiting = !unavailable && Number.isInteger(raw.waiting_count) && raw.waiting_count >= 0
        ? raw.waiting_count
        : null
    const isCurrent = raw.is_current === true
    const switchTarget = normalizeTarget(raw.switch_target)
    return {
        id: raw.company_id,
        key: String(raw.company_id),
        name,
        letter: firstLetter(name),
        waiting,
        unavailable: unavailable || waiting === null,
        asOf: parseTime(raw.as_of),
        warning: raw.data_quality === 'warning',
        isCurrent,
        canOpen: raw.can_open_chat === true && (isCurrent || switchTarget !== null),
        switchTarget,
    }
}

export function normalizeSummary(body) {
    const data = unwrap(body)
    if (!Array.isArray(data.companies)) throw contractError('companies')
    const pollIntervalMs = positiveMs(data.poll_interval_ms, DEFAULT_POLL_MS, MIN_POLL_MS)
    const currentCompanyId = isId(body.current_company_id) ? body.current_company_id : data.current_company_id
    return {
        scopeId: isId(data.scope_id) ? String(data.scope_id) : null,
        canManage: data.can_manage === true,
        emptyReason: typeof data.empty_reason === 'string' ? data.empty_reason : null,
        pollIntervalMs,
        staleAfterMs: positiveMs(data.stale_after_ms, DEFAULT_STALE_MS, pollIntervalMs),
        serverNow: parseTime(body.server_now),
        currentCompanyId: isId(currentCompanyId) ? String(currentCompanyId) : null,
        settingsVersion: body.settings_version !== undefined ? body.settings_version : data.settings_version,
        companies: data.companies.map(normalizeCompany),
    }
}

function normalizeEntry(raw) {
    if (!isPlainObject(raw) || !isId(raw.entry_id)) throw contractError('entry_id')
    const available = raw.availability !== 'unavailable' && isId(raw.company_id)
    const name = available && typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : null
    return {
        entryId: raw.entry_id,
        key: String(raw.entry_id),
        companyId: available ? raw.company_id : null,
        name,
        letter: name ? firstLetter(name) : '?',
        available: available && name !== null,
        canRemove: raw.can_remove === true,
        canReorder: raw.can_reorder === true,
        sortOrder: Number(raw.sort_order),
    }
}

export function normalizeSettings(body) {
    const data = unwrap(body)
    if (!Array.isArray(data.entries)) throw contractError('entries')
    const entries = data.entries.map(normalizeEntry)
    if (entries.every(entry => Number.isFinite(entry.sortOrder))) {
        entries.sort((a, b) => a.sortOrder - b.sortOrder)
    }
    return {
        scopeId: isId(data.scope_id) ? String(data.scope_id) : null,
        scopeKind: data.scope_kind === 'personal' ? 'personal' : 'shared',
        version: data.version,
        canManage: data.can_manage === true,
        entries,
    }
}

export function normalizeAvailable(body) {
    const data = unwrap(body)
    if (!Array.isArray(data.items)) throw contractError('items')
    const items = data.items
        .filter(item => isPlainObject(item) && isId(item.company_id))
        .map(item => ({
            id: item.company_id,
            key: String(item.company_id),
            name: typeof item.name === 'string' && item.name.trim() ? item.name.trim() : 'Без названия',
            isSelected: item.is_selected === true,
        }))
    return {
        items,
        page: Number.isInteger(data.page) ? data.page : 1,
        hasMore: data.has_more === true,
    }
}

export function retryAfterMs(error) {
    const ms = Number(error && error.retry_after_ms)
    return Number.isFinite(ms) && ms > 0 ? ms : null
}
